import { useState, useContext } from "react";
import { useForm, Controller } from "react-hook-form";
import { useAddressAutoComplete } from "../../state/addressAutoCompleteState";
import { useAddress } from "../../state/addressState";
import { useDebounce } from "../../hooks/useDebounce";
import { useSaveAddress } from "../../hooks/useSaveAddress";
// State
import { useUserBranches } from "../../state/userBranchListState";
// Context
import { AlertNotificationContext } from "../../contexts/AlertNotificationContext";
// Hooks
import { useCheckAPI } from "../../hooks/useCheckAPI";
import { useSaveOrder } from "../../state/draftOrderSaveState";
import { useProtectedContent } from "../../hooks/useProtectedContent";
// Components
import { RHFSelectField } from "../../components/RHFSelectField";
// MUI
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Select from "@mui/material/Select";
import FormHelperText from "@mui/material/FormHelperText";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Autocomplete from "@mui/material/Autocomplete";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import Skeleton from "@mui/material/Skeleton";

export const DeliveryAddressCreate = ({ order, setTab }) => {
  const [searchAddress, setSearchAddress] = useState("");
  const [searchAddressID, setSearchAddressID] = useState("");
  const debouncedValue = useDebounce(searchAddress, 1000);
  const [addressOptions, setAddressOptions] = useState([]);
  const { setAlertNotification } = useContext(AlertNotificationContext);

  const allowCreateAddress = useProtectedContent("addressCreate");

  // Form
  let defaultValues = {
    name: "",
    address: "",
    address2: "",
    county: "",
    city: "",
    postcode: "",
    country: "GB",
    residential: false,
    contact: "",
    contactEmail: "",
    contactMobile: "",
    showOnPortal: true,
    branchNo: "",
    what3words: "",
  };
  const { handleSubmit, reset, control, setValue, watch } = useForm({
    defaultValues,
  });
  const watchResidential = watch("residential", false);

  //  Call get address by ID
  const { data: addressData, isSuccess: addressSuccess } = useAddress(
    {
      params: {
        id: searchAddressID,
      },
    },
    searchAddressID ? true : false,
  );

  // Success Function
  const addressSuccessFunc = () => {
    setValue("address", addressData.data.line_1);
    setValue("address2", addressData.data.line_2);
    setValue("county", addressData.data.county);
    setValue("city", addressData.data.town_or_city);
    setValue("postcode", addressData.data.postcode);
    setValue("country", changeCountryFormat(addressData.data.country));
    setValue("residential", !!addressData.data.residential);
  };

  // Error Function
  const addressErrorFunc = () =>
    setAlertNotification({
      message: `Error: ${addressData.status}: ${addressData.statusText}`,
      severity: "error",
    });

  // Hook to check the BC API for errors
  useCheckAPI(
    addressSuccess,
    addressData,
    addressData?.status,
    addressSuccessFunc,
    addressErrorFunc,
  );

  // Call autocomplete
  const {
    data: autoCompleteData,
    isSuccess: autocompleteSuccess,
    isError: autocompleteError,
  } = useAddressAutoComplete(
    {
      params: {
        address: debouncedValue,
      },
    },
    debouncedValue && debouncedValue.length >= 3 ? true : false,
  );

  // Success Function
  const autocompleteSuccessFunc = () => {
    if (autoCompleteData?.data) {
      setAddressOptions(autoCompleteData.data);
    }
  };

  // Error Function
  const autocompleteErrorFunc = () => {
    setAddressOptions([]); // Clear options on error
    setAlertNotification({
      message: `Error: ${autoCompleteData?.status || "Unknown"}: ${autoCompleteData?.statusText || "Address lookup failed"}`,
      severity: "error",
    });
  };

  // Hook to check the BC API for errors
  useCheckAPI(
    autocompleteSuccess && !autocompleteError,
    autoCompleteData,
    autoCompleteData?.status,
    autocompleteSuccessFunc,
    autocompleteErrorFunc,
  );

  // useEffect(() => {
  //   if (isSuccess) {
  //     setAddressOptions(data.data);
  //   }
  // }, [isSuccess, data]);

  // const handleAddressLookup = (e) => {
  //   const address = e.target.value;
  //   // if (address) {
  //   setSearchAddress(address);
  //   // } else {
  //   //   setAddressOptions([]);
  //   // }
  // };

  const onInputChange = (e) => {
    const address = e.target.value;
    if (address && address.length >= 3) {
      setSearchAddress(address);
    } else {
      setSearchAddress("");
      setAddressOptions([]);
    }
  };

  const handleAddressSearch = (event, value) => {
    setSearchAddressID(value.id);
  };

  const validationHelper = {
    name: { required: "Name is required" },
    address: { required: "Address is required" },
    address2: { required: "Address 2 is required" },
    county: { required: "County is required" },
    city: { required: "City is required" },
    postcode: { required: "Postcode is required" },
    country: { required: "Country is required" },
    contact: { required: "Contact is required" },
    contactEmail: {
      required: "Contact Email is required",
      pattern: "Invalid email address",
    },
    contactMobile: { required: "Contact Mobile is required" },
    branchNo: { required: "Branch is required" },
  };

  // Save address
  const {
    mutate,
    isSuccess: addressSaveSuccess,
    data: addressSaveData,
  } = useSaveAddress();

  const handleOnSubmit = (data) => {
    mutate({
      data: {
        filter: { code: "" },
        address: {
          contact: data.contact,
          name: data.name,
          address: data.address,
          address2: data.address2,
          city: data.city,
          county: data.county,
          postCode: data.postcode,
          country: data.country,
          homeaddress: !!data.residential,
          email: data.contactEmail,
          mobileNo: data.contactMobile,
          showOnPortal: data.showOnPortal,
          branchNo: data.branchNo,
          what3words: data.what3words,
        },
      },
    });
  };

  // Success Function
  const successFunc = () => {
    reset(defaultValues);
    setAlertNotification({
      message: `Address added`,
      severity: "info",
    });
    // Add this address to the order
    handleSaveOrder(addressSaveData);
  };

  // Error Function
  const errorFunc = () =>
    setAlertNotification({
      message: `Error: ${addressSaveData.data.status}: ${addressSaveData.data.statusText}`,
      severity: "error",
    });

  // Hook to check the BC API for errors
  useCheckAPI(
    addressSaveSuccess,
    addressSaveData,
    addressSaveData?.data?.status,
    successFunc,
    errorFunc,
  );

  // Convert country returned by API to country code compatible with BC
  const changeCountryFormat = (country) => {
    if (
      country === "England" ||
      country === "Wales" ||
      country === "Scotland" ||
      country === "Northern Ireland"
    ) {
      return "GB";
    } else {
      return "";
    }
  };

  //   Save the order
  const {
    mutate: saveOrder,
    isSuccess: saveSuccess,
    data: saveData,
  } = useSaveOrder();

  // Handle
  const handleSaveOrder = (address) => {
    const newAddress = address.data.data.data.data.address;
    // Merge the new address object with the existing order object
    let merged = { ...order.data.order, ...newAddress };
    // Remove name2, showOnPortal, and branchNo because they are not supported
    delete merged.name2;
    delete merged.showOnPortal;
    delete merged.branchNo;
    saveOrder({
      data: {
        filter: order.data.filter,
        order: merged,
        staff: order.data.staff,
      },
    });
  };

  // Do this on save order success
  const saveSuccessFunc = () => {
    setTab(0);
  };

  // Do this on save order error
  const saveErrorFunc = () =>
    setAlertNotification({
      message: `Error: ${saveData.data.status}: ${saveData.data.statusText}`,
      severity: "error",
    });

  // Hook to check the BC API for errors
  useCheckAPI(
    saveSuccess,
    saveData,
    saveData?.data?.status,
    saveSuccessFunc,
    saveErrorFunc,
  );

  const NameInput = () => {
    return (
      <Controller
        name="name"
        control={control}
        rules={{
          required: true,
        }}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            fullWidth
            size="small"
            label="Name / Company"
            error={error !== undefined}
            helperText={error ? validationHelper.name[error.type] : ""}
          />
        )}
      />
    );
  };

  const Address1Input = () => {
    return (
      <Controller
        name="address"
        control={control}
        rules={{
          required: true,
        }}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            fullWidth
            size="small"
            label="Address Line 1"
            error={error !== undefined}
            helperText={error ? validationHelper.address[error.type] : ""}
          />
        )}
      />
    );
  };

  const Address2Input = () => {
    return (
      <Controller
        name="address2"
        control={control}
        rules={{
          required: false,
        }}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            fullWidth
            size="small"
            label="Address Line 2"
            error={error !== undefined}
            helperText={error ? validationHelper.address2[error.type] : ""}
          />
        )}
      />
    );
  };

  const CountyInput = () => {
    return (
      <Controller
        name="county"
        control={control}
        rules={{
          required: false,
        }}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            fullWidth
            size="small"
            label="County"
            error={error !== undefined}
            helperText={error ? validationHelper.county[error.type] : ""}
          />
        )}
      />
    );
  };

  const CityInput = () => {
    return (
      <Controller
        name="city"
        control={control}
        rules={{
          required: true,
        }}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            fullWidth
            size="small"
            label="City"
            error={error !== undefined}
            helperText={error ? validationHelper.city[error.type] : ""}
          />
        )}
      />
    );
  };

  const PostcodeInput = () => {
    return (
      <Controller
        name="postcode"
        control={control}
        rules={{
          required: true,
        }}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            fullWidth
            size="small"
            label="Postcode"
            error={error !== undefined}
            helperText={error ? validationHelper.postcode[error.type] : ""}
          />
        )}
      />
    );
  };

  const HomeInput = () => {
    return (
      <Controller
        name="residential"
        control={control}
        render={({ field: { onChange, value } }) => (
          <FormControlLabel
            control={<Switch checked={value} onChange={onChange} />}
            label="Residential Address"
          />
        )}
      />
    );
  };

  const ContactInput = () => {
    return (
      <Controller
        name="contact"
        control={control}
        rules={{
          required: watchResidential,
        }}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            fullWidth
            size="small"
            label="Contact Name"
            error={error !== undefined}
            helperText={error ? validationHelper.contact[error.type] : ""}
          />
        )}
      />
    );
  };

  const ContactMobileInput = () => {
    return (
      <Controller
        name="contactMobile"
        control={control}
        rules={{
          required: true,
        }}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            fullWidth
            size="small"
            label="Contact Mobile"
            error={error !== undefined}
            helperText={error ? validationHelper.contactMobile[error.type] : ""}
          />
        )}
      />
    );
  };

  const ContactEmailInput = () => {
    return (
      <Controller
        name="contactEmail"
        control={control}
        rules={{
          required: true,
          pattern: /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
        }}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            fullWidth
            size="small"
            label="Contact Email"
            error={error !== undefined}
            helperText={error ? validationHelper.contactEmail[error.type] : ""}
          />
        )}
      />
    );
  };

  const countryArray = ["", "GB", "IE"];
  const CountryInput = ({ countryArray }) => {
    return (
      <Controller
        name="country"
        control={control}
        rules={{
          required: true,
        }}
        render={({ field, fieldState: { error } }) => (
          <FormControl fullWidth size="small" error={error ? true : false}>
            <InputLabel id="country-label">Country</InputLabel>
            <Select
              labelId="country-label"
              {...field}
              label="Country"
              error={error !== undefined}
            >
              {countryArray.map((country) => {
                return (
                  <MenuItem key={country} value={country}>
                    {country}
                  </MenuItem>
                );
              })}
            </Select>
            {error ? (
              <FormHelperText>
                {validationHelper.country[error.type]}
              </FormHelperText>
            ) : null}
          </FormControl>
        )}
      />
    );
  };

  const { data: dataBranches } = useUserBranches();
  const branchArray = dataBranches?.data.data.data.list.branch;
  const BranchSelect = () => {
    return (
      <>
        {dataBranches ? (
          <RHFSelectField
            name="branchNo"
            label="Branch"
            options={branchArray}
            optionIdentifiers={{ id: "no", label: "name" }}
            control={control}
            validationHelper={validationHelper}
            rules={{ required: false }}
            viewMode={false}
            size="small"
          />
        ) : (
          <Skeleton variant="rounded" width="100%" height={40} />
        )}
      </>
    );
  };

  return (
    <Paper
      sx={{
        width: "100%",
      }}
    >
      {allowCreateAddress ? (
        <Grid
          component="form"
          autoComplete="off"
          onSubmit={handleSubmit(handleOnSubmit)}
          container
          spacing={2}
          sx={{
            p: 2,
          }}
        >
          <Grid size={12}>
            <Typography variant="h6">Create new address</Typography>
          </Grid>
          <Grid size={12}>
            <Autocomplete
              onInputChange={onInputChange}
              onChange={handleAddressSearch}
              filterOptions={(x) => x}
              freeSolo
              id="search-address"
              disableClearable
              size="small"
              options={addressOptions || []}
              getOptionLabel={(option) => option?.address || ""}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search address.."
                  slotProps={{
                    ...params.slotProps,

                    input: {
                      ...params.slotProps.input,
                      type: "search",
                    },
                  }}
                />
              )}
            />
          </Grid>
          <Grid size={12}>
            <Divider />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <NameInput />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <Address1Input />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <Address2Input />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <CountyInput />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <CityInput />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <PostcodeInput />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <CountryInput countryArray={countryArray} />
          </Grid>

          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <ContactInput />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <BranchSelect />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
            sx={{
              display: "flex",
              justifyContent: "left",
              alignItems: "center",
              pl: 2,
            }}
          >
            <HomeInput />
          </Grid>
          {watchResidential && (
            <>
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                }}
              >
                <ContactEmailInput />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 6,
                }}
              >
                <ContactMobileInput />
              </Grid>
            </>
          )}
          <Grid size={12}>
            <Stack
              direction="row-reverse"
              spacing={2}
              sx={{
                justifyContent: "flex-start",
                alignItems: "center",
              }}
            >
              <Button type="submit" variant="contained">
                Save
              </Button>
              <Button onClick={() => reset()} variant="outlined">
                Clear
              </Button>
            </Stack>
          </Grid>
        </Grid>
      ) : (
        <Typography>You do not have access to create new addresses.</Typography>
      )}
    </Paper>
  );
};
